import { memo, useMemo, useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import SplitLayout from "@core/layout/SplitLayout";
import { Grid2, IconButton, Tooltip, Typography } from "@mui/material";
import SelectionCard from "../components/SelectionCard";
import MarkdownRenderer from "@/domains/core/components/MarkdownRenderer";
import { FETCH_TAXONOMY_SECTORS, FETCH_TAXONOMY_MAPPING } from "@extras/graphql/queries/fetchTaxonomyData";
import { icon } from "@/app/style/icons";

export interface TaxonomyActivity {
    name: string;
    sector: string;
    activity_id: string;
    nace: string;
    contributions: {
        category: string;
        description: string;
        substant_contrib_criteria: string;
        dnsh: {
            [key: string]: string;
        };
        footnotes?: string;
    }[];
}

export interface TaxonomyMapping {
    activities: TaxonomyActivity[];
    totalCount: number;
}

const CompassPage = memo(() => {
    // Selection states
    const [selectedSector, setSelectedSector] = useState<string>("");
    const [selectedActivity, setSelectedActivity] = useState<string>("");
    const [selectedGoal, setSelectedGoal] = useState<string>("");

    // Fetch sectors
    const { data: sectorData, loading: loadingSectors } = useQuery<{ taxonomySectors: string[] }>(FETCH_TAXONOMY_SECTORS);

    // Fetch filtered mapping data
    const { data: mappingData, loading: loadingMapping } = useQuery<{ taxonomyMapping: TaxonomyMapping }>(FETCH_TAXONOMY_MAPPING, {
        variables: {
            sector: selectedSector || undefined,
            activity: selectedActivity || undefined,
            goal: selectedGoal || undefined,
        },
        skip: !selectedSector && !selectedActivity && !selectedGoal,
    });

    // Compute available activities for current selection
    const availableActivities = useMemo(() => {
        if (!mappingData?.taxonomyMapping) return [];
        return [...new Set(mappingData.taxonomyMapping.activities.map((a) => a.name))].sort();
    }, [mappingData]);

    // Compute available goals for current selection
    const availableGoals = useMemo(() => {
        if (!mappingData?.taxonomyMapping) return [];
        const goals = new Set<string>();
        mappingData.taxonomyMapping.activities.forEach((activity) => {
            activity.contributions.forEach((c) => goals.add(c.category));
        });
        return [...goals].sort();
    }, [mappingData]);

    // Get selected activity details
    const selectedActivityDetails = useMemo(() => {
        if (!mappingData?.taxonomyMapping || !selectedActivity) return null;
        return mappingData.taxonomyMapping.activities.find((a) => a.name === selectedActivity);
    }, [mappingData, selectedActivity]);

    // Get selected contribution details
    const selectedContribution = useMemo(() => {
        if (!selectedActivityDetails || !selectedGoal) return null;
        return selectedActivityDetails.contributions.find((c) => c.category === selectedGoal);
    }, [selectedActivityDetails, selectedGoal]);

    // Clear activity and goal whenever the sector changes
    useEffect(() => {
        if (selectedActivity !== "" || selectedGoal !== "") {
            setSelectedActivity("");
            setSelectedGoal("");
        }
    }, [selectedSector]);

    // Reset goal whenever the activity changes (regardless of data loading state)
    useEffect(() => {
        if (selectedGoal !== "") {
            setSelectedGoal("");
        }
    }, [selectedActivity]);

    return (
        <SplitLayout
            containerLeft={
                <Grid2 container size={12} spacing={3} pt={7.5}>
                    <Grid2 size={12}>
                        <SelectionCard
                            title="Sektor"
                            description="Wähle einen Sektor:"
                            options={sectorData?.taxonomySectors || []}
                            selectedOption={selectedSector}
                            setSelectedOption={setSelectedSector}
                            disabled={loadingSectors}
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <SelectionCard
                            title="Aktivität"
                            description="Wähle eine Aktivität:"
                            options={availableActivities}
                            selectedOption={selectedActivity}
                            setSelectedOption={setSelectedActivity}
                            disabled={!selectedSector}
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <SelectionCard
                            title="Taxonomie-Ziel"
                            description="Wähle ein Ziel:"
                            options={availableGoals}
                            selectedOption={selectedGoal}
                            setSelectedOption={setSelectedGoal}
                            disabled={!selectedActivity}
                        />
                    </Grid2>
                </Grid2>
            }
            containerRight={
                <Grid2 container size={12} spacing={3}>
                    <Grid2 container size={12} direction={"row"} alignContent={"center"} spacing={1.5}>
                        <Typography variant="h3">EU-Taxonomie-Kompass</Typography>

                        <Tooltip
                            title={
                                "Der EU-Taxonomie-Kompass ist ein interaktives Werkzeug für die EU-Taxonomie. Hier können relevante Wirtschaftstätigkeiten der EU-Taxonomie für die jeweiligen Branchen über das Auswahlmenu auf der linken Seite angezeigt werden."
                            }
                        >
                            <IconButton>
                                <icon.info sx={{ color: "semantic.icon.informative" }} />
                            </IconButton>
                        </Tooltip>
                    </Grid2>

                    {selectedActivityDetails && selectedContribution && (
                        <Grid2 container size={12} spacing={2.5}>
                            <Grid2 size={12}>
                                <Typography variant="h4">{selectedGoal}</Typography>
                            </Grid2>

                            <Grid2 container size={12} spacing={1}>
                                <Grid2 size={12}>
                                    <Typography fontWeight={600}>NACE:</Typography>
                                </Grid2>
                                <Grid2 size={12}>
                                    <Typography>{selectedActivityDetails.nace}</Typography>
                                </Grid2>
                            </Grid2>

                            <Grid2 container size={12} spacing={1}>
                                <Grid2 size={12}>
                                    <Typography fontWeight={600}>Beschreibung:</Typography>
                                </Grid2>
                                <Grid2 size={12}>
                                    <Typography>{selectedContribution.description}</Typography>
                                </Grid2>
                            </Grid2>

                            <Grid2 container size={12} spacing={1}>
                                <Grid2 size={12}>
                                    <Typography fontWeight={600}>Wesentliche Kriterien:</Typography>
                                </Grid2>
                                <Grid2 size={12}>
                                    <MarkdownRenderer>{selectedContribution.substant_contrib_criteria}</MarkdownRenderer>
                                </Grid2>
                            </Grid2>

                            {Object.entries(selectedContribution.dnsh).map(([criterion, text]) => (
                                <Grid2 container size={12} spacing={1} key={criterion}>
                                    <Grid2 size={12}>
                                        <Typography fontWeight={600}>{criterion}:</Typography>
                                    </Grid2>
                                    <Grid2 size={12}>
                                        <MarkdownRenderer>{text}</MarkdownRenderer>
                                    </Grid2>
                                </Grid2>
                            ))}
                        </Grid2>
                    )}
                </Grid2>
            }
        />
    );
});

export default CompassPage;
